<template>
    <!-- One fixed-width cell per property so every row lines up: an active reset
         button when the property carries a user change, a muted dot when it is
         still on its default/inherited value, and an empty (but reserved) cell
         when the property has no default to fall back to at all.
         `resettable` left unset renders nothing — that keeps the shared form
         controls unchanged everywhere outside the Property Editor. -->
    <div v-if="resettable !== null" class="flex w-[1.125rem] shrink-0 items-center justify-end">
        <button v-if="resettable && modified" type="button"
            class="flex h-4 w-4 items-center justify-center rounded text-blue-400 hover:bg-blue-500/25 hover:text-blue-200 focus:outline-none"
            :title="title" :aria-label="title"
            @click="$emit('reset')">
            <font-awesome-icon icon="fa-solid fa-rotate-left" class="text-[10px]" />
        </button>
        <span v-else-if="resettable" class="flex h-4 w-4 items-center justify-center text-slate-500"
            :title="title" :aria-label="title">
            <font-awesome-icon icon="fa-solid fa-circle" class="text-[5px]" />
        </span>
    </div>
</template>

<script>
import { overrideStatusTitle } from './overrideStatus.js';

// Replaces the old `reset` / `inherited` text badges: same information, same
// reset handler, but a constant-width icon column (see overrideStatus.js for
// the wording rules). Purely presentational — it never reads or writes a
// property itself, and it knows nothing about styles vs top-level properties:
// the caller decides what "modified" means for its property type.
export default {
    name: 'PropertyOverrideIndicator',

    props: {
        // null (the default) = this control is not property-status-aware, render
        // nothing. false = the property has no default/unset state, so the cell
        // stays empty but reserved. true = show the status.
        resettable: { type: Boolean, default: null },
        // The property carries an explicit user change (node.hasExplicitStyle()
        // for styles, "differs from its effective default" for top-level
        // properties — see objectPropertyStatus.js).
        modified: { type: Boolean, default: false },
        // True when the fallback is another user-visible scope (a non-default
        // state inheriting the part's default), false for schema/theme defaults.
        inherited: { type: Boolean, default: false },
    },

    emits: ['reset'],

    computed: {
        title() {
            return overrideStatusTitle(this.modified, this.inherited);
        },
    },
};
</script>
